/**
 * 内联快捷操作组件
 * 简单操作无需弹窗，直接内联执行
 */

import { Loader2 } from 'lucide-react'
import { type ReactNode } from 'react'

import { cn } from '@/lib/utils'

// ==================== 类型定义 ====================

type ActionVariant = 'primary' | 'success' | 'warning' | 'danger'

interface InlineActionProps {
  label: string
  onClick: () => void
  variant?: ActionVariant
  loading?: boolean
  disabled?: boolean
  icon?: ReactNode
  className?: string
}

// ==================== 样式配置 ====================

const VARIANT_STYLES: Record<ActionVariant, string> = {
  primary:
    'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50',
  success:
    'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50',
  warning:
    'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50',
  danger:
    'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50',
}

// ==================== 组件实现 ====================

export function InlineAction({
  label,
  onClick,
  variant = 'primary',
  loading = false,
  disabled = false,
  icon,
  className,
}: InlineActionProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT_STYLES[variant],
        className
      )}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : icon}
      {label}
    </button>
  )
}

/**
 * 内联操作组
 */
interface InlineActionGroupProps {
  children: ReactNode
  className?: string
}

export function InlineActionGroup({ children, className }: InlineActionGroupProps) {
  return <div className={cn('flex flex-wrap gap-2', className)}>{children}</div>
}

/**
 * 工作流操作组件
 * 提供常用的工作流操作按钮
 */
interface WorkflowActionsProps {
  onSubmit?: () => void
  onApprove?: () => void
  onReject?: () => void
  onRollback?: () => void
  submitLoading?: boolean
  approveLoading?: boolean
  showSubmit?: boolean
  showApprove?: boolean
  showReject?: boolean
  showRollback?: boolean
  className?: string
}

export function WorkflowActions({
  onSubmit,
  onApprove,
  onReject,
  onRollback,
  submitLoading = false,
  approveLoading = false,
  showSubmit = true,
  showApprove = true,
  showReject = false,
  showRollback = false,
  className,
}: WorkflowActionsProps) {
  return (
    <InlineActionGroup className={className}>
      {showSubmit && onSubmit && (
        <InlineAction
          label={submitLoading ? '提交中...' : '提交审核'}
          onClick={onSubmit}
          variant="primary"
          loading={submitLoading}
        />
      )}
      {showApprove && onApprove && (
        <InlineAction
          label={approveLoading ? '审批中...' : '审批通过'}
          onClick={onApprove}
          variant="success"
          loading={approveLoading}
        />
      )}
      {showRollback && onRollback && (
        <InlineAction label="回退轨道" onClick={onRollback} variant="warning" />
      )}
      {showReject && onReject && <InlineAction label="回退" onClick={onReject} variant="danger" />}
    </InlineActionGroup>
  )
}
